"use client"
import React, { useEffect, useState } from "react"
import { Content } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import Section from "@aura-design/system/section"
import Grid from "@aura-design/system/grid"
import Input from "@aura-design/system/input"
import Select from "@aura-design/system/select"
import Button from "@aura-design/system/button"
import Alert from "@aura-design/system/alert"
import { useForm, useStatus } from "@aura-design/system/form"
import { SymbolIcon } from "@radix-ui/react-icons"
import useDebounce from "@airplan/hooks/debounce"
import useCertificates from "@airplan/hooks/certificates"
import { getUniqueValues } from "@airplan/utils/data-view"

/**
 * Props for `CertificatesSearch`.
 */
export type CertificatesSearchProps =
  SliceComponentProps<Content.CertificatesSearchSlice>

/**
 * Component for "CertificatesSearch" Slices.
 */
const CertificatesSearch = ({
  slice,
}: CertificatesSearchProps): JSX.Element => {
  const [files, setFiles] = useState([])

  const status = useStatus()

  const formData = useForm({
    nit: "",
    year: "",
    retentionType: "",
  })

  const { nit, year, retentionType } = formData
  const debouncedValue = useDebounce(nit.value)
  const { data, isLoading, isError, isValidating } = useCertificates({
    nit: debouncedValue.value,
  })

  const uniqueValues = getUniqueValues(data)

  useEffect(() => {
    year?.reset()
    retentionType?.reset()
    setFiles([])
  }, [debouncedValue?.value])

  const handleOnSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    status.resetStatus()
    status.setIsLoading(true)

    if (!nit.value || !year.value || !retentionType.value) {
      status.setIsError(true)
      status.setMessage("Todos los campos son obligatorios.")
    }

    try {
      const filteredData = data.filter(
        (item) =>
          item.nit === nit.value &&
          item.year === year.value &&
          item.retentionType === retentionType.value,
      )

      if (!filteredData) {
        status.setIsError(true)
        status.setMessage("Ha ocurrido un error al buscar los certificados.")
        return
      }

      setFiles(filteredData)
      status.setIsLoading(false)
    } catch (error) {
      status.setIsLoading(false)
    }
  }

  const handleOnDownload = async (file) => {
    try {
      const response = await fetch(`/certificates/${file.name}`)
      const blob = await response.blob()
      // Create a URL for the blob
      const url = URL.createObjectURL(blob)
      // Create an anchor tag and click it to trigger the download
      const a = document.createElement("a")
      a.href = url
      a.download = file.name // Set the filename
      a.click()
      URL.revokeObjectURL(url) // Clean up
      status.setIsLoading(false)
    } catch (error) {
      console.error("Error downloading file:", error)
      status.setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isValidating) {
      nit?.dialog(null)
      return
    }

    if(nit?.value.length < 6) {
      nit?.dialog(null)
      return
    }

    if (nit.value && data?.length > 0) {
      nit?.dialog(null)
    } else {
      nit?.dialog("NIT no encontrado.")
    }
  }, [nit.value, isValidating])

  useEffect(() => {
    setFiles(null)
  }, [year?.value, retentionType?.value])

  return (
    <Section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      container="smesh"
      space="aura"
    >
      <div className="shadow bg-white aura">
        <form onSubmit={handleOnSubmit}>
          <Grid col="four">
            <div>
              <label>NIT</label>
              <Input type="text" pattern="[0-9]*" {...nit} />
            </div>
            <div>
              <label>Año</label>
              <Select
                {...year}
                placeholder="Seleccionar"
                isDisabled={!Boolean(uniqueValues?.year?.length > 0)}
              >
                {uniqueValues?.year?.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label>Tipo de retención</label>
              <Select
                {...retentionType}
                placeholder="Seleccionar"
                isDisabled={!Boolean(uniqueValues?.retentionType?.length > 0)}
              >
                {uniqueValues?.retentionType?.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Button
                label="Buscar"
                className="mt-1.5 bg-dark-blue border-dark-blue hover:border-dark-yellow"
                isFluid
                isLoadingText={
                  <>
                    <SymbolIcon className="icon animate-spin" />
                  </>
                }
                isLoading={isValidating || debouncedValue.isLoading}
              />
            </div>
          </Grid>
        </form>
        <Alert state={status.state} />
      </div>
      {!files ? null : files?.length > 0 ? (
        <Grid col="one" className="mt-1">
          {files.map((file, index) => (
            <div className="aura shadow" key={index}>
              <div>
                <h3 className="h6 text-dark-blue">{file.name}</h3>
                <div className="mt-0.5">
                  <span className="bg-primary text-white wall-pad mr-1 rounded-md">
                    <strong>NIT: {file.nit}</strong>
                  </span>
                  <span className="bg-primary text-white wall-pad mr-1 rounded-md">
                    <strong>Año: {file.year}</strong>
                  </span>
                  <span className="bg-primary text-white wall-pad mr-1 rounded-md">
                    <strong>Tipo de retención: {file.retentionType}</strong>
                  </span>
                </div>
              </div>
              <div className="mt-1">
                <Button
                  label="Descargar"
                  className="bg-dark-blue border-dark-blue hover:border-dark-yellow"
                  onClick={() => handleOnDownload(file)}
                />
              </div>
            </div>
          ))}
        </Grid>
      ) : (
        <div className="text-center">
          {!isValidating && retentionType.value && year.value && nit?.value && <p>No se encontraron certificados {retentionType.value ? `de ${retentionType.value}` : ""}  {year.value ? `para el año ${year.value}` : ""}.</p>}
        </div>
      )}
    </Section>
  )
}

export default CertificatesSearch
